/**
 * The custom request headers that carry browser-side telemetry context to the server.
 *
 * Isomorphic on purpose: the constants are shared by the client (which attaches the headers to every
 * fetch-based call) and the server (which reads them back in `browserContextFields`), and nothing
 * here may import server-only modules — this file ends up in the client bundle.
 *
 * These ride on fetch-based calls ONLY: server functions and tRPC. A document navigation cannot set
 * request headers, which is why a `pageview` event carries timezone/viewport/app-version as null and
 * gets its client context from the native headers (Accept-Language, Sec-CH-UA*) instead.
 */
import { APP_VERSION_STORAGE_KEY } from './app-version';

export const TIMEZONE_HEADER = 'x-timezone';
export const VIEWPORT_HEADER = 'x-viewport';
export const APP_VERSION_TELEMETRY_HEADER = 'x-app-version';
export const TRACE_ID_HEADER = 'x-trace-id';
export const SESSION_ID_HEADER = 'x-session-id';
export const ROOT_SPAN_ID_HEADER = 'x-root-span-id';

/** Per-tab session id, minted on first use. sessionStorage, so a new tab is a new session. */
const SESSION_ID_STORAGE_KEY = 'worldcoo:telemetry-session-id';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Printable ASCII, which is all a header value may hold. Enforced HERE rather than at each consumer
 * because `Headers.set` (and tRPC's own header handling) THROWS on anything else, and these headers ride
 * every server-function and tRPC call: an unrepresentable value must cost us one attribute, never the
 * call. `x-app-version` is the realistic source — it comes back out of localStorage, where a corrupted
 * or hand-edited value would otherwise take the whole app's data layer down with it.
 */
const SAFE_HEADER_VALUE = /^[\x20-\x7e]+$/;

/**
 * A correlation id (trace, span, session) from an inbound header, or undefined if it isn't one.
 *
 * These headers are attacker-controlled and land verbatim in durable storage. Every id we mint is a
 * `crypto.randomUUID()`, so requiring that shape costs nothing legitimate while bounding the column's
 * width and cardinality and keeping trace-grafting to "can guess a uuid". A rejected value just means
 * the event is uncorrelated, which is the pre-existing behaviour for a request with no header at all.
 */
export function asCorrelationId(value: string | null | undefined): string | undefined {
	return value && UUID_RE.test(value) ? value : undefined;
}

declare global {
	interface Window {
		__TRACE_ID?: string;
		__ROOT_SPAN_ID?: string;
		/** The path + search this document was RENDERED for; see `SsrTrace.url` (TEC-3854). */
		__RENDER_URL?: string;
	}
}

/**
 * The id identifying this tab's visit. Survives navigations within the tab (sessionStorage) so a
 * user's whole trail links together, but never crosses tabs or outlives the tab — it is a debugging
 * correlation key, not a tracking identifier, and deliberately isn't a cookie.
 *
 * Falls back to a per-page-load id when storage is unavailable (Safari private mode throws on
 * sessionStorage access), which degrades the correlation window rather than breaking telemetry.
 */
let inMemorySessionId: string | undefined;

/**
 * `crypto.randomUUID` only exists in a secure context, so it is absent on a plain-http preview or
 * LAN address. Not worth a polyfill for a correlation key, but it MUST NOT throw: this runs on the
 * path of every server-function call, and an exception here would fail the call itself.
 */
function randomId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return '';
	}
}

export function getSessionId(): string {
	if (typeof window === 'undefined') return '';
	// Memoized on the happy path too, not just the storage-blocked one: this runs per outbound call, and
	// the id cannot change without a page load.
	if (inMemorySessionId) return inMemorySessionId;
	try {
		const existing = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
		if (existing) return (inMemorySessionId = existing);
		const minted = randomId();
		if (minted) window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, minted);
		return (inMemorySessionId = minted);
	} catch {
		return (inMemorySessionId = randomId());
	}
}

/**
 * Resolved once per page load. Constructing an `Intl.DateTimeFormat` costs ~26µs — 4000× the cost of
 * reading a cached string, and several times that on a mid-range phone — and this used to run on every
 * server-function and tRPC call, so a donation page spent a millisecond of main-thread time re-deriving
 * a value that cannot change without a reload.
 */
const timezone = ((): string | undefined => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		// Intl unavailable or the platform has no zone database.
		return undefined;
	}
})();

/**
 * The telemetry headers to attach to a client-initiated request. Every value is best-effort: a
 * missing one just means the corresponding attribute is null on the event, never a failed request.
 */
export function telemetryHeaders(): Record<string, string> {
	if (typeof window === 'undefined') return {};
	const headers: Record<string, string> = {};
	// Each read is independently guarded, and an unrepresentable value is dropped rather than thrown:
	// `Headers.set` rejects anything outside printable ASCII, and this rides every server-function call.
	const put = (name: string, read: () => string | null | undefined): void => {
		try {
			const value = read();
			if (value && SAFE_HEADER_VALUE.test(value)) headers[name] = value;
		} catch {
			// Intl, window metrics or storage unavailable — the attribute is simply null on the event.
		}
	};
	put(TIMEZONE_HEADER, () => timezone);
	put(VIEWPORT_HEADER, () => `${String(window.innerWidth)}x${String(window.innerHeight)}`);
	// The version of the bundle actually RUNNING here, which is not necessarily the version the server is
	// on — a tab left open across a deploy is exactly the case worth being able to see. Read fresh, since
	// `syncVersionFromHeader` rewrites it mid-session.
	put(APP_VERSION_TELEMETRY_HEADER, () => window.localStorage.getItem(APP_VERSION_STORAGE_KEY));
	// Baked into the document by the SSR render (see __root.tsx head()), so every call this page makes
	// joins the trace that rendered it.
	put(TRACE_ID_HEADER, () => window.__TRACE_ID);
	put(ROOT_SPAN_ID_HEADER, () => window.__ROOT_SPAN_ID);
	put(SESSION_ID_HEADER, getSessionId);
	return headers;
}
